import { useState } from 'react';
import moment from 'moment';
import { useFormik } from 'formik';
import { Icon } from '@iconify/react';
import { useSnackbar } from 'notistack';
import {
  Grid,
  Dialog,
  Tooltip,
  IconButton,
  DialogTitle,
  DialogContent,
  TextField
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import closeOutline from '@iconify-icons/eva/close-outline';

import { CloseIcon } from '@/common/icons/CloseIcon';
import { MIconButton } from '../../../../../../../@material-extend';
import { AddCreditModalSchema } from '../../../../../../../schemas';
import AuthUserStore from '../../../../../../../common/utils/AuthUser';
import { CreditTypeSelector } from '../../../../../../../common/Selectors';
import { DateInput } from '@/components/DateInput';
import {
  ELEVATED_ROLE_ACTIONS,
  useElevatedRoleAction
} from '@/hooks/useElevatedRoleAction';
import { useStaffGrantCredits } from '@/api/finance/staff';

interface AddCreditsModalProps {
  open: boolean;
  closeModal: (close?: boolean) => void;
  customerId: string;
}

const AddCreditsModal = ({
  open,
  closeModal,
  customerId
}: AddCreditsModalProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { isReady, transport } = useElevatedRoleAction({
    actionType: ELEVATED_ROLE_ACTIONS.ADD_CREDITS,
    onCancel: () => closeModal(false),
    promptMessage: "Enter manager's pin to add credits."
  });
  const currentAuthUser = AuthUserStore.getAuthUser();

  const { mutateAsync: grantCredits } = useStaffGrantCredits({
    request: { axiosInstance: transport }
  });

  const defaultExpire = moment().add(100, 'd').format('YYYY-MM-DD');

  const formik = useFormik({
    initialValues: {
      amount: 1,
      creditType: '',
      customer: customerId,
      reason: 'Added by manager',
      store: currentAuthUser?.store?.id,
      expires: defaultExpire
    },
    enableReinitialize: true,
    validationSchema: AddCreditModalSchema,
    onSubmit: async values => {
      setIsSubmitting(true);
      try {
        await grantCredits({
          data: {
            customerId: parseInt(values.customer),
            creditTypeId: parseInt(values.creditType),
            quantity: values.amount,
            expires: values.expires,
            reason: values.reason
          }
        });

        const message = values.amount > 1 ? 'Credits were' : 'Credit was';

        enqueueSnackbar(`${values.amount} ${message} added`, {
          variant: 'success',
          action: (key: string | number) => (
            <MIconButton size="small" onClick={() => closeSnackbar(key)}>
              <CloseIcon />
            </MIconButton>
          )
        });
        closeModal(true);
      } catch (e: any) {
        const error = e?.response?.data?.error_code || '';
        enqueueSnackbar('There was an error adding Credits ' + error, {
          variant: 'error'
        });
      } finally {
        setIsSubmitting(false);
      }
    }
  });

  return (
    <Dialog
      open={open}
      onClose={() => closeModal(false)}
      maxWidth="sm"
      fullWidth={true}
    >
      <DialogTitle
        style={{
          display: 'flex',
          alignItems: 'center',
          background: '#fafafa',
          padding: '10px 15px 10px 30px',
          justifyContent: 'space-between'
        }}
      >
        <h3>Add Credits</h3>
        <Tooltip arrow title="Close">
          <IconButton onClick={() => closeModal(false)}>
            <Icon icon={closeOutline} />
          </IconButton>
        </Tooltip>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} mt={2}>
          <Grid item xs={6}>
            <TextField
              fullWidth
              type="number"
              label="Quantity"
              InputLabelProps={{
                shrink: true
              }}
              {...formik.getFieldProps('amount')}
              error={Boolean(formik.touched.amount && formik.errors.amount)}
              helperText={formik.touched.amount && formik.errors.amount}
            />
          </Grid>
          <Grid item xs={6}>
            <CreditTypeSelector
              value={formik.values.creditType}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                formik.setFieldValue('creditType', e.target.value)
              }
              removeDials
              error={Boolean(
                formik.touched.creditType && formik.errors.creditType
              )}
              helperText={formik.touched.creditType && formik.errors.creditType}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              type="text"
              name="reason"
              label="Reason"
              value={formik.values.reason}
              inputProps={{ maxLength: 255 }}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                formik.handleChange(e)
              }
              InputLabelProps={{
                shrink: true,
                required: true
              }}
              error={Boolean(formik.touched.reason && formik.errors.reason)}
              helperText={
                formik.touched.reason && formik.errors.reason
                  ? formik.errors.reason
                  : '*Default Value: Added by manager'
              }
            />
          </Grid>
        </Grid>
        <Grid item xs={12} pt={3}>
          <DateInput
            fullWidth
            variant="outlined"
            name="expires"
            label="Select Next Expiration Date"
            InputLabelProps={{ shrink: true, required: true }}
            inputProps={{ min: moment().format('YYYY-MM-DD') }}
            value={formik.values.expires}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              formik.handleChange(e)
            }
          />
        </Grid>
      </DialogContent>
      <div
        style={{
          width: '100%',
          padding: '10px',
          display: 'flex',
          background: '#fafafa',
          justifyContent: 'flex-end'
        }}
      >
        <LoadingButton
          type="submit"
          variant="outlined"
          loading={!isReady || isSubmitting}
          disabled={!isReady}
          onClick={formik.submitForm}
        >
          Add Credits
        </LoadingButton>
      </div>
    </Dialog>
  );
};

export default AddCreditsModal;
